import styled from "styled-components/macro";
import { useQueryClient } from "@tanstack/react-query";
import SidePanel, { SidePanelTrigger } from "./SidePanel";
import {
  BoxHeader,
  BoxContent,
  BoxNavRight,
  BoxNavButton,
  BoxNavLeft,
  BoxTitle,
  Box,
} from "./Box";
import { Close } from "./Icons";
import Button from "./Button";
import Title from "./Title";
import { useToast } from "../hooks/use-toast";
import { touchSkipioApi, useSkipioApi } from "../api";
import Input, { Label } from "./Input";
import Composer from "./Composer";
import { useFields } from "../hooks/use-fields";
import Stack from "./Stack";
import Combobox from "./Combobox";

function MessageLibrarySidePanel({
  trigger,
  sharedSmsMessageTemplateId,
}: {
  trigger: SidePanelTrigger;
  sharedSmsMessageTemplateId?: string;
}) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => (
        <SavedMessageEditor
          hide={hide}
          sharedSmsMessageTemplateId={sharedSmsMessageTemplateId}
        />
      )}
    />
  );
}

function SavedMessageEditor({
  sharedSmsMessageTemplateId,
  hide,
}: {
  sharedSmsMessageTemplateId?: string;
  hide: () => void;
}) {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();

  const template = sharedSmsMessageTemplateId
    ? skipioApi.sharedSmsMessageTemplates[sharedSmsMessageTemplateId].get().data
    : null;

  const { field, value, onChange } = useFields({
    initialValue: {
      name: template ? template.name : "",
      body: template ? template.body : "",
      imageUrl: template && template.mediaUrls ? template.mediaUrls[0] : "",
      imageType:
        template && template.mediaUrls ? template.mediaMimeTypes[0] : "",
      tags: template ? template.tags : [],
    },
  });

  const showToast = useToast();

  async function save() {
    const body = {
      name: value.name,
      body: value.body,
      mediaUrls: value.imageUrl ? [value.imageUrl] : null,
      mediaMimeTypes: value.imageType ? [value.imageType] : null,
      tags: value.tags,
    };

    if (template) {
      await skipioApi.sharedSmsMessageTemplates[template.id].put(
        {},
        { data: body }
      );
    } else {
      await skipioApi.sharedSmsMessageTemplates.post({}, { data: body });
    }

    await touchSkipioApi("sharedSmsMessageTemplates");
    await queryClient.invalidateQueries({
      queryKey: ["sharedSmsMessageTemplates"],
    });
    showToast("Saved");
    hide();
  }

  return (
    <>
      <BoxHeader>
        <BoxNavLeft>
          <BoxTitle>{template ? "Edit" : "New"} Message Template</BoxTitle>
        </BoxNavLeft>
        <BoxNavRight>
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxContent>
        <Stack>
          <Box elevated>
            <BoxContent>
              <Stack>
                <Title>
                  Message Details
                  <small>
                    Fill in basic details to organize and locate your message.
                  </small>
                </Title>
                <Input label="Title" placeholder="Name" {...field("name")} />
                <>
                  <Label>
                    Message Body
                    <small>
                      Type in the message you'd like to save. You can attach
                      images, links, and merge tags to the message.
                    </small>
                  </Label>
                  <Composer
                    enableMessageLibrary={false}
                    enableRinglessVoicemail={false}
                    enableScheduling={false}
                    value={value}
                    onChange={(patch) => {
                      onChange((existingValue) => {
                        const newValue =
                          typeof patch === "function"
                            ? {
                                ...existingValue,
                                // @ts-expect-error
                                ...patch(existingValue),
                              }
                            : patch;

                        return newValue;
                      });
                    }}
                  />
                </>
              </Stack>
            </BoxContent>
          </Box>
          <Box elevated>
            <BoxContent>
              <Stack>
                <Title>
                  Keywords
                  <small>
                    Attribute keywords to this message to quickly locate this
                    message, and return smart recommended quick responses based
                    on the conversation you're having with the contact.
                    (optional)
                  </small>
                </Title>
                <Combobox
                  label="keywords"
                  {...field("tags")}
                  allowString
                  placeholder="Add a keyword"
                  addLabel="Add Keyword"
                />
              </Stack>
            </BoxContent>
          </Box>
        </Stack>
        <Footer>
          <Button onClick={save}>Save</Button>
        </Footer>
      </BoxContent>
    </>
  );
}

export default MessageLibrarySidePanel;

const Footer = styled.div`
  margin-top: 20px;
  text-align: center;
`;
